import classNames from 'classnames'
import { createRef, useEffect, useRef, useState } from 'react'
import Button from 'react-bootstrap/Button'
import BSCard from 'react-bootstrap/Card'
import { useCookies } from 'react-cookie'
import { forceCheck } from 'react-lazyload'

import Scrollspy from '@/components/Scrollspy/Scrollspy'
import Spinner from '@/components/Spinner/Spinner'
import cookieService from '@/services/cookie.service'
import { TOGGLE_PERSIST } from '@/store/cardlists/actions/types'
import { update } from '@/store/cardlists/actions/update'
import { useAppSelector } from '@/store/hooks'
import { store } from '@/store/store'
import { FilterType } from '@/types/additionalFilters'
import { CardListType, PageCardType } from '@/types/card'
import { defaultGroupedBy, defaultSortedBy } from '@/utils/constants'
import { BORDER_WIDTH, CARD_WIDTH, GRID_GAP } from '@/utils/constants'
import { modeKeyToValue } from '@/utils/helpers'

import AdditionalFilters from './AdditionalFilters/AdditionalFilters'
import CardListAlerts from './CardListAlerts/CardListAlerts'
import styles from './CardLists.module.scss'
import CardListsHeader from './CardListsHeader/CardListsHeader'
import CardView from './CardView/CardView'
import Sidebar from './CardView/Sidebar'
import TableView from './TableView/TableView'
import TextView from './TextView/TextView'

type Props = {
  card?: PageCardType
  cardViewOnly?: boolean
  disabledSortMethods?: string[]
  enableCount?: boolean
  groupCardLists?: boolean
  hideControls?: boolean
  hideSidebar?: boolean
  initCardLists?: CardListType[]
  showSearch?: boolean
  topLeftEdge?: boolean
}

const CardLists = ({
  card,
  cardViewOnly,
  disabledSortMethods,
  enableCount,
  groupCardLists,
  hideControls,
  hideSidebar,
  initCardLists,
  showSearch,
  topLeftEdge,
}: Props) => {
  const {
    cardLists: storeCardLists,
    filters: storeFilters,
    groupedBy: storeGroupedBy,
    loading,
    persist,
    sortedBy: storeSortedBy,
    updatedAt,
  } = useAppSelector((state) => state.cardlists)

  const filters = hideControls ? [] : storeFilters
  const groupedBy = hideControls ? defaultGroupedBy : storeGroupedBy
  const sortedBy = hideControls ? defaultSortedBy : storeSortedBy
  const [cookies, _] = useCookies(['cardListView'])
  const ref = useRef<HTMLAnchorElement>(null)
  const rightRef = useRef<HTMLDivElement>(null)
  const [filterForm, setFilterForm] = useState<FilterType[]>([])
  const [numCols, setNumCols] = useState<number>(0)
  const [sectionRefs, setSectionRefs] = useState<React.RefObject<HTMLDivElement>[]>([])
  const [sidebarExpanded, setSidebarExpanded] = useState(false)

  const cardLists = (updatedAt ? storeCardLists : initCardLists) as CardListType[]
  const view = cookies.cardListView || 'card'

  const handleResize = () => {
    if (rightRef.current) {
      const offsetWidth = rightRef.current.offsetWidth
      const newNumCols = groupCardLists
        ? (offsetWidth - CARD_WIDTH - 2 * GRID_GAP - 2 * BORDER_WIDTH) /
            (CARD_WIDTH + 3 * GRID_GAP + 2 * BORDER_WIDTH) +
          1
        : offsetWidth / CARD_WIDTH
      setNumCols(Math.floor(newNumCols))
    }
  }

  const togglePersist = () => {
    const newPersist = !persist
    store.dispatch({ type: TOGGLE_PERSIST })
    if (newPersist) {
      cookieService.set('groupedBy', groupedBy)
      cookieService.set('sortedBy', sortedBy)
      cookieService.set('filters', filters)
    } else {
      cookieService.remove('groupedBy')
      cookieService.remove('sortedBy')
      cookieService.remove('filters')
    }
  }

  const scrollToCardLists = () => {
    if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    // Initialize filter form on load
    setFilterForm(filters)

    // Recheck LazyLoad elements on window resize
    window.addEventListener('resize', forceCheck)
    return () => window.removeEventListener('resize', forceCheck)
  }, [])

  // Initialize cardlists store
  useEffect(() => {
    update({ groupedBy, initCardLists, sortedBy })
  }, [initCardLists])

  useEffect(() => {
    if (cardLists?.length > 0) {
      setSectionRefs(cardLists.map(() => createRef<HTMLDivElement>()))
    }
  }, [cardLists])

  // Recheck LazyLoad elements on cardlist update
  useEffect(() => {
    if (!loading) {
      forceCheck()
    }
  }, [loading])

  // Recheck grid size on sidebar expansion
  useEffect(() => {
    handleResize()
  }, [sidebarExpanded])

  if (!initCardLists) return null

  const numResults = cardLists.reduce((acc, { cardviews }) => acc + cardviews.length, 0)

  const scrollspyChildren = ({ currentElementIndexInViewport }: { currentElementIndexInViewport: number }) => (
    <>
      {!hideSidebar && view === 'card' && cardLists.length > 1 && (
        <Sidebar
          cardLists={cardLists}
          currentElementIndexInViewport={currentElementIndexInViewport}
          handleResize={handleResize}
          setSidebarExpanded={setSidebarExpanded}
          sidebarExpanded={sidebarExpanded}
          topLeftEdge={topLeftEdge}
        />
      )}
      <div className='position-relative w-100'>
        <CardListsHeader
          cardLists={cardLists}
          currentElementIndexInViewport={currentElementIndexInViewport}
          disabledSortMethods={disabledSortMethods}
          enableCount={enableCount}
          filters={filterForm}
          groupedBy={groupedBy}
          hideControls={hideControls}
          loading={loading}
          persist={persist}
          setFilters={setFilterForm}
          sortedBy={sortedBy}
          togglePersist={togglePersist}
          updatedAt={updatedAt}
        />
        <div className='d-none d-sm-flex w-100'>
          {filterForm.length > 0 && (
            <div className={styles.borderBottom}>
              <AdditionalFilters className='m-2' filters={filterForm} setFilters={setFilterForm} />
            </div>
          )}
        </div>
        <CardListAlerts filters={filterForm} groupedBy={groupedBy} loading={loading} sortedBy={sortedBy} />
        {loading && (
          <div className={styles.spinner}>
            <BSCard>
              <BSCard.Body className='align-items-center d-flex flex-column gap-2'>
                <div className='m-2'>
                  <Spinner size='lg' />
                </div>
                {groupedBy !== 'default' && (
                  <div>
                    Grouping by <b>{(modeKeyToValue(groupedBy, 'group') || 'unknown').toLowerCase()}</b>
                  </div>
                )}
                {sortedBy !== 'default' && (
                  <div>
                    Sorting by <b>{(modeKeyToValue(sortedBy, 'sort') || 'unknown').toLowerCase()}</b>
                  </div>
                )}
                <Button
                  className='mt-2'
                  onClick={() => update({ groupedBy: defaultGroupedBy, persist: false, sortedBy: defaultSortedBy })}
                  size='sm'
                  variant='danger'
                >
                  Reset
                </Button>
              </BSCard.Body>
            </BSCard>
          </div>
        )}
        {(filterForm.length > 0 || filters.length > 0) && (
          <div className='d-flex justify-content-center m-3 text-gray w-100'>
            <i>
              {numResults} <span>{numResults !== 1 ? 'cards' : 'card'}</span> found
            </i>
          </div>
        )}
        {(cardViewOnly || view === 'card') && (
          <div className='m-2'>
            <CardView
              cardLists={cardLists}
              enableCount={enableCount}
              groupCardLists={groupCardLists}
              handleResize={handleResize}
              hideSidebar={hideSidebar}
              loading={loading}
              numCols={numCols}
              pageCommander={card}
              rightRef={rightRef}
              scrollToCardLists={scrollToCardLists}
              sectionRefs={sectionRefs}
              showSearch={showSearch}
            />
          </div>
        )}
        {!cardViewOnly && view === 'text' && <TextView cardLists={cardLists} loading={loading} />}
        {!cardViewOnly && view === 'table' && <TableView cardLists={cardLists} loading={loading} />}
      </div>
    </>
  )

  return (
    <BSCard
      className={classNames('mvCardList shadow-sm', styles.mvCardList)}
      style={{
        borderTopLeftRadius: topLeftEdge ? 0 : undefined,
        borderTopRightRadius: view === 'text' ? 0 : undefined,
      }}
    >
      <a className={styles.top} id='cardlists' ref={ref} />
      <div className='d-flex'>
        <Scrollspy offset={-100} sectionRefs={sectionRefs}>
          {scrollspyChildren}
        </Scrollspy>
      </div>
    </BSCard>
  )
}

export default CardLists
